import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Search, Building2, User, ShieldAlert, X, Loader2, Truck } from "lucide-react";

export default function AdminSearch({ onSection }) {
  const { setViewAs } = useAuth();
  const navigate = useNavigate();
  const boxRef = useRef(null);
  const gen = useRef(0);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const myGen = ++gen.current; // bumps on EVERY query change, before the debounce
    const term = q.trim();
    if (term.length < 2) { setResults(null); setError(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/search?q=${encodeURIComponent(term)}`);
        if (myGen !== gen.current) return; // a newer keystroke superseded this request
        setResults(data);
        setError(false);
        setOpen(true);
      } catch {
        if (myGen !== gen.current) return;
        setResults({ accounts: [], users: [] });
        setError(true);
        setOpen(true);
      } finally {
        if (myGen === gen.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const reset = () => { gen.current++; setQ(""); setResults(null); setError(false); setOpen(false); };

  const openAccount = (a) => { setViewAs(a.id, a.company_name); onSection?.("accounts"); reset(); };
  const openUser = () => { onSection?.("users"); reset(); };
  const fileClaim = (a) => { setViewAs(a.id, a.company_name); reset(); navigate("/claims"); };

  const hasResults = results && (results.accounts.length || results.users.length);

  return (
    <div ref={boxRef} className="relative w-full sm:w-96" data-testid="admin-search">
      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input
        data-testid="admin-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results && setOpen(true)}
        placeholder="Search accounts, users, orders…"
        className="w-full pl-9 pr-8 py-2.5 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
      />
      {(loading || q) && (
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> :
            <button onClick={reset} data-testid="admin-search-clear" aria-label="Clear admin search"><X className="w-4 h-4 text-slate-400 hover:text-slate-700" /></button>}
        </span>
      )}

      {open && q.trim().length >= 2 && (
        <div data-testid="admin-search-results" className="absolute z-50 mt-2 w-full sm:w-[28rem] bg-white border border-slate-200 rounded-xl shadow-xl max-h-[70vh] overflow-y-auto">
          {error ? (
            <div className="p-4 text-sm" data-testid="admin-search-error">
              <p className="text-red-600 font-medium">Couldn&apos;t run the search.</p>
              <button onClick={() => setQ((v) => v + " ")} data-testid="admin-search-retry" className="mt-1 text-blue-600 hover:text-blue-700 font-semibold text-sm">Retry</button>
            </div>
          ) : !hasResults ? (
            <p className="p-4 text-sm text-slate-400">{loading ? "Searching…" : `No matches for "${q.trim()}"`}</p>
          ) : (
            <>
              {results.accounts.length > 0 && (
                <div className="p-2">
                  <p className="px-2 py-1 text-[11px] uppercase tracking-wider font-bold text-slate-400">Accounts</p>
                  {results.accounts.map((a) => (
                    <div key={a.id} data-testid={`search-account-${a.id}`} className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-slate-50">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0"><Building2 className="w-4 h-4" /></div>
                      <button onClick={() => openAccount(a)} data-testid={`search-open-account-${a.id}`} className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-semibold text-slate-900 truncate">{a.company_name}</p>
                        <p className="text-xs text-slate-400 truncate">{a.boxhero_account_id || a.primary_email}{a.has_store ? "" : " · no store"}</p>
                      </button>
                      <button onClick={() => fileClaim(a)} data-testid={`search-file-claim-${a.id}`} title="File a claim for this store"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700 border border-orange-200 hover:border-orange-400 rounded-md px-2 py-1 shrink-0">
                        <ShieldAlert className="w-3.5 h-3.5" /> Claim
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {results.users.length > 0 && (
                <div className="p-2 border-t border-slate-100">
                  <p className="px-2 py-1 text-[11px] uppercase tracking-wider font-bold text-slate-400">Users</p>
                  {results.users.map((u) => (
                    <button key={u.id} data-testid={`search-user-${u.id}`} onClick={openUser}
                      className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-slate-50 text-left">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><User className="w-4 h-4" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{u.name || u.email}</p>
                        <p className="text-xs text-slate-400 truncate">{u.email}{u.account_name ? ` · ${u.account_name}` : ""}</p>
                      </div>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${u.status === "active" ? "bg-emerald-100 text-emerald-700" : u.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{u.status}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="px-4 py-2 border-t border-slate-100 flex items-center gap-1.5 text-[11px] text-slate-400">
                <Truck className="w-3.5 h-3.5" /> Tip: pick a store, then use its Claims page to search orders and file a claim.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
